import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "./useCart";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { ga4 } from "../analytics/ga4";
import { formatEuro } from "../lib/format";
import gsap from "gsap";

function formatPrice(cents: number): string {
  return formatEuro(cents);
}

export function CartDrawer() {
  const { t } = useTranslation();
  const { locale } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { items, isOpen, setOpen, totalCents, quote, removeItem, updateQuantity, clearCart } = useCart();
  const isMobile = useIsMobile();
  const cfg = useSiteConfig();

  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  // Cambiando pagina il carrello si chiude (solo a un cambio vero, non al montaggio).
  const pathname = location.pathname;
  const lastPathRef = useRef(pathname);
  useEffect(() => {
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    setOpen(false);
  }, [pathname, setOpen]);

  // Il pannello resta montato dall'apertura fino alla fine dell'animazione di
  // uscita. L'accensione è derivata durante il render (nessun effetto che la
  // rincorra); lo spegnimento arriva a fine animazione.
  if (isOpen && !visible) setVisible(true);

  // GSAP: animazione di uscita
  useEffect(() => {
    if (isOpen || !visible) return;
    const tl = gsap.timeline({
      onComplete: () => setVisible(false),
    });
    if (panelRef.current) {
      tl.to(panelRef.current, { x: "100%", duration: 0.35, ease: "power2.inOut" }, 0);
    }
    if (backdropRef.current) {
      tl.to(backdropRef.current, { opacity: 0, duration: 0.35, ease: "power2.inOut" }, 0);
    }
    tlRef.current = tl;
  }, [isOpen, visible]);

  // entrance animation after visible becomes true
  useEffect(() => {
    if (!visible || !isOpen) return;
    // Wait one frame for DOM to mount
    const rafId = requestAnimationFrame(() => {
      const tl = gsap.timeline();

      if (backdropRef.current) {
        tl.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.35, ease: "power2.out" }, 0);
      }
      if (panelRef.current) {
        tl.fromTo(panelRef.current, { x: "100%" }, { x: "0%", duration: 0.45, ease: "expo.out" }, 0);
      }

      // Stagger cart items
      const itemEls = panelRef.current?.querySelectorAll(".cart-item-enter");
      if (itemEls && itemEls.length > 0) {
        tl.fromTo(
          itemEls,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.35, stagger: 0.06, ease: "power2.out" },
          0.2
        );
      }

      // Footer slide up
      if (footerRef.current) {
        tl.fromTo(
          footerRef.current,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.35, ease: "power2.out" },
          itemEls && itemEls.length > 0 ? 0.2 + itemEls.length * 0.06 : 0.3
        );
      }

      tlRef.current = tl;
    });

    return () => cancelAnimationFrame(rafId);
  }, [visible, isOpen]);

  // Cleanup timeline on unmount
  useEffect(() => {
    return () => {
      tlRef.current?.kill();
    };
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // Pill di contatto per il box "confezione personalizzata" — coerente con il drawer.
  const contactPillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 12px",
    borderRadius: 50,
    border: "1px solid rgba(28,75,59,0.2)",
    background: cfg.color_body_bg,
    color: cfg.color_dark,
    fontSize: 12,
    fontWeight: 500,
    textDecoration: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1,
  };

  if (!isOpen && !visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="cart-backdrop-enter"
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.2)",
          backdropFilter: "blur(2px)",
          zIndex: 110,
          opacity: 0,
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="cart-panel-enter"
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100%",
          width: isMobile ? "100vw" : 400,
          background: cfg.color_body_bg,
          borderLeft: isMobile ? "none" : "1px solid rgba(28,75,59,0.89)",
          zIndex: 120,
          display: "flex",
          flexDirection: "column",
          fontFamily: cfg.font_body,
          transform: "translateX(100%)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "18px 24px",
            borderBottom: "1px solid rgba(28,75,59,0.89)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: cfg.color_light_bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={cfg.color_dark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 01-8 0" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, color: cfg.color_dark }}>{t("cart.title")}</div>
              <div style={{ fontSize: 11, color: "rgba(28,75,59,0.86)", marginTop: 1 }}>
                {items.length} {items.length === 1 ? t("cart.itemSingular") : t("cart.itemPlural")}
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            aria-label={t("cart.close")}
            style={{
              background: "none",
              border: "1px solid rgba(28,75,59,0.89)",
              width: 32,
              height: 32,
              borderRadius: 8,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(28,75,59,0.86)",
              transition: "border-color 0.2s",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {items.length === 0 ? (
            <div style={{ textAlign: "center", marginTop: 60 }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={cfg.color_muted} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16, opacity: 0.5 }}>
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 01-8 0" />
              </svg>
              <p style={{ color: "rgba(28,75,59,0.86)", fontSize: 14 }}>{t("cart.empty")}</p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="cart-item-enter"
                style={{
                  background: cfg.color_light_bg,
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", gap: 14 }}>
                  {item.image && (
                    <img
                      src={resolveImageUrl(item.image)}
                      alt=""
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: 8,
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 500, fontSize: 14, color: cfg.color_dark, margin: 0, lineHeight: 1.3 }}>{item.title}</p>
                    <p style={{ fontWeight: 500, fontSize: 15, marginTop: 6, color: cfg.color_dark, margin: "6px 0 0" }}>
                      {formatPrice(item.price_cents * item.quantity)}
                    </p>
                  </div>
                </div>

                {item.subItems && item.subItems.length > 0 && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(28,75,59,0.89)" }}>
                    {item.subItems.map((sub, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: i < item.subItems!.length - 1 ? 6 : 0 }}>
                        {sub.image && (
                          <img src={sub.image} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                        )}
                        <span style={{ fontSize: 12, color: "rgba(28,75,59,0.86)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub.title}</span>
                        <span style={{ fontSize: 11, color: "rgba(28,75,59,0.86)", flexShrink: 0 }}>{formatPrice(sub.price_cents)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    style={{
                      border: "1px solid rgba(28,75,59,0.89)",
                      width: 32,
                      height: 32,
                      cursor: "pointer",
                      background: cfg.color_body_bg,
                      fontSize: 16,
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: cfg.color_dark,
                    }}
                  >
                    -
                  </button>
                  <span style={{ fontSize: 14, fontWeight: 500, minWidth: 24, textAlign: "center", color: cfg.color_dark }}>{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    style={{
                      border: "1px solid rgba(28,75,59,0.89)",
                      width: 32,
                      height: 32,
                      cursor: "pointer",
                      background: cfg.color_body_bg,
                      fontSize: 16,
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: cfg.color_dark,
                    }}
                  >
                    +
                  </button>
                  <button
                    onClick={() => removeItem(item.id)}
                    style={{
                      marginLeft: "auto",
                      background: "none",
                      border: "none",
                      color: "rgba(28,75,59,0.86)",
                      fontSize: 12,
                      cursor: "pointer",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                    {t("cart.remove")}
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Box "confezione personalizzata" — invito al contatto commerciale (non bloccante) */}
          {items.length > 0 && (
            <div
              style={{
                marginTop: 4,
                background: cfg.color_light_bg,
                border: "1px solid rgba(28,75,59,0.1)",
                borderLeft: `3px solid ${cfg.color_accent}`,
                borderRadius: 8,
                padding: "14px 16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cfg.color_accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="20 12 20 22 4 22 4 12" />
                  <rect x="2" y="7" width="20" height="5" />
                  <line x1="12" y1="22" x2="12" y2="7" />
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                </svg>
                <p style={{ fontSize: 13, fontWeight: 600, color: cfg.color_dark, margin: 0, lineHeight: 1.3 }}>
                  {t("cart.customTitle")}
                </p>
              </div>
              <p style={{ fontSize: 12, color: "rgba(28,75,59,0.75)", margin: "0 0 12px", lineHeight: 1.5 }}>
                {t("cart.customDesc")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { ga4.contactClick("chat"); setOpen(false); const detail = { intent: "custom", items: items.slice(0, 12).map((i) => ({ slug: i.slug, qty: i.quantity, title: i.title })) }; window.setTimeout(() => window.dispatchEvent(new CustomEvent("gb:open-contact", { detail })), 360); }}
                  style={{ ...contactPillStyle, background: cfg.color_dark, color: "#fff", border: "none" }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                  {t("cart.customChat")}
                </button>
              </div>

              {/* Contatti in chiaro (numero + email) sotto il pulsante — stessa fonte del footer */}
              {(cfg.contact_phone || cfg.contact_email) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(28,75,59,0.12)" }}>
                  {cfg.contact_phone && (
                    <a href={`tel:${cfg.contact_phone.replace(/\s/g, "")}`} onClick={() => ga4.contactClick("phone")} style={{ display: "inline-flex", alignItems: "center", gap: 8, color: cfg.color_dark, textDecoration: "none", fontSize: 13.5, fontWeight: 600, letterSpacing: 0.2 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg.color_accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                      {cfg.contact_phone}
                    </a>
                  )}
                  {cfg.contact_email && (
                    <a href={`mailto:${cfg.contact_email}`} onClick={() => ga4.contactClick("email")} style={{ display: "inline-flex", alignItems: "center", gap: 8, color: cfg.color_dark, textDecoration: "none", fontSize: 13.5, fontWeight: 600, letterSpacing: 0.2, wordBreak: "break-all" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cfg.color_accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>
                      {cfg.contact_email}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div
            ref={footerRef}
            style={{
              borderTop: "1px solid rgba(28,75,59,0.89)",
              padding: "20px 24px",
              flexShrink: 0,
            }}
          >
            {/* Quantità minima per pacco — un solo riquadro chiaro */}
            {quote && quote.lines.some((l) => !l.moq_met) && (
              <div style={{ background: "#FDECEC", border: "1px solid #F5B5B5", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: "#8A1F1F", margin: "0 0 3px" }}>
                  {t("cart.moqTitle")}
                </p>
                <p style={{ fontSize: 11, color: "#8A1F1F", opacity: 0.85, margin: "0 0 9px", lineHeight: 1.45 }}>
                  {t("cart.moqDesc")}
                </p>
                {quote.lines.filter((l) => !l.moq_met).map((l) => (
                  <div key={l.product_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 7 }}>
                    <span style={{ fontSize: 11.5, color: "#8A1F1F" }}>
                      {l.title}: <strong>{l.quantity} / {l.moq} {t("cart.pcs")}</strong>
                    </span>
                    {l.moq != null && (
                      <button
                        onClick={() => updateQuantity(l.product_id, l.moq as number)}
                        style={{ background: "#8A1F1F", color: "#fff", border: "none", borderRadius: 5, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}
                      >
                        {t("cart.bringTo", { n: l.moq })}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Riepilogo importi */}
            <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(28,75,59,0.75)" }}>
                <span>{t("cart.subtotal")}</span>
                <span>{formatPrice(quote ? quote.subtotal_cents : totalCents)}</span>
              </div>
              {quote && quote.total_discount_cents > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#1c7a45", fontWeight: 500 }}>
                  <span>{t("cart.discount")}</span>
                  <span>&minus;{formatPrice(quote.total_discount_cents)}</span>
                </div>
              )}
              {quote && quote.vat_rate_percent > 0 && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(28,75,59,0.75)" }}>
                    <span>{t("cart.taxableBase")}</span>
                    <span>{formatPrice(quote.taxable_cents)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(28,75,59,0.75)" }}>
                    <span>{t("cart.vat", { rate: quote.vat_rate_percent })}</span>
                    <span>{formatPrice(quote.vat_cents)}</span>
                  </div>
                </>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 8, borderTop: "1px solid rgba(28,75,59,0.12)" }}>
                <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", color: cfg.color_muted }}>
                  {quote && quote.vat_rate_percent > 0 ? t("cart.totalInclVat") : t("cart.total")}
                </span>
                <span style={{ fontWeight: 600, fontSize: 22, color: cfg.color_dark }}>
                  {formatPrice(quote ? (quote.vat_rate_percent > 0 ? quote.total_gross_cents : quote.total_cents) : (cfg.vat_rate_percent > 0 ? totalCents + Math.round((totalCents * cfg.vat_rate_percent) / 100) : totalCents))}
                </span>
              </div>
              <p style={{ fontSize: 10.5, color: "rgba(28,75,59,0.5)", margin: "2px 0 0", textAlign: "right" }}>
                {t("cart.shippingVatNote")}
              </p>
            </div>

            <button
              onClick={() => {
                if (quote && !quote.can_checkout) return;
                // NB: begin_checkout NON parte da qui. Questo click porta solo alla
                // pagina di checkout (e per chi non è loggato al login): emetterlo
                // qui gonfierebbe il funnel con chi non arriva mai al pagamento.
                // L'evento è emesso in Checkout.tsx a sessione Stripe creata.
                setOpen(false);
                navigate(`/${locale || "it"}/checkout`);
              }}
              disabled={!!quote && !quote.can_checkout}
              style={{
                width: "100%",
                background: quote && !quote.can_checkout ? "#bdbdb5" : cfg.color_dark,
                color: "#fff",
                border: "none",
                padding: "14px 24px",
                cursor: quote && !quote.can_checkout ? "not-allowed" : "pointer",
                fontWeight: 500,
                marginBottom: 8,
                borderRadius: 8,
                fontSize: 13,
                letterSpacing: 1,
                textTransform: "uppercase",
                transition: "opacity 0.2s",
                opacity: 1,
              }}
              onMouseEnter={(e) => { if (!(quote && !quote.can_checkout)) e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              {t("cart.checkout")}
            </button>
            <button
              onClick={() => {
                if (window.confirm(t("cart.clearConfirm"))) {
                  clearCart();
                }
              }}
              style={{
                width: "100%",
                background: "transparent",
                color: "rgba(28,75,59,0.86)",
                border: "1px solid rgba(28,75,59,0.89)",
                padding: "10px 0",
                cursor: "pointer",
                fontSize: 12,
                borderRadius: 8,
                fontWeight: 400,
                transition: "border-color 0.2s",
              }}
            >
              {t("cart.clear")}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
